import React from 'react';
import PropTypes from 'prop-types';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { postStripeSetupIntent, postStripeConciergeSetupIntent } from '../../api/api';
import TranslationProvider from '../../providers/TranslationProvider';
import StripeAddCardForm from '../StripeAddCardForm';
import './StripeForm.scss';
import { STRIPE_FORM_CONTEXT } from '../../constants/paymentProcessors';

const appearance = (isMobile) => ({
  rules: {
    '.Input': {
      padding: '13px 10px',
      boxShadow: 'none',
      borderRadius: '6px',
    },
    '.Input::selection': {
      backgroundColor: '#B4D5FE',
    },
    '.Input::selection, .Input::placeholder': {
      fontFamily: "'GT America', Helvetica, Verdana, sans-serif",
      fontSize: '16px',
    },
    '.Label': {
      fontFamily: "'Beatrice', 'GT America', Helvetica, Verdana, sans-serif",
      fontSize: '12px',
      fontWeight: 'bold',
      marginBottom: '10px',
    },
  },

  variables: {
    colorText: '#2a2a2a',
    spacingGridColumn: isMobile ? '15px' : '20px',
    spacingGridRow: '20px',
  },
});

class StripeForm extends React.Component {
  static propTypes = {
    Analytics: PropTypes.shape({
      event: PropTypes.func.isRequired,
    }).isRequired,
    AutomationMode: PropTypes.bool.isRequired,
    closeForm: PropTypes.func.isRequired,
    matchMedia: PropTypes.shape({
      xs: PropTypes.bool,
      sm: PropTypes.bool,
      md: PropTypes.bool,
      lg: PropTypes.bool,
      xl: PropTypes.bool,
    }).isRequired,
    redirectPath: PropTypes.string,
    stripePublishableKey: PropTypes.string.isRequired,
    withHeader: PropTypes.bool,
    prepayAccountId: PropTypes.string,
    prepayClientKey: PropTypes.string,
    context: PropTypes.string,
    venueId: PropTypes.number,
  }

  static defaultProps = {
    redirectPath: '/account/payment-methods',
    withHeader: true,
    prepayClientKey: null,
    prepayAccountId: null,
    context: null,
    venueId: null,
  }

  state = {
    clientSecret: undefined,
    stripeAccountId: null,
    error: undefined,
    stripePromise: null,
  }

  async componentDidMount() {
    const {
      context,
      prepayAccountId,
      prepayClientKey,
      venueId,
    } = this.props;

    const { clientSecret, stripePromise } = this.state;

    if (prepayClientKey && prepayAccountId) {
      this.setState(
        { clientSecret: prepayClientKey, stripeAccountId: prepayAccountId }
      );
      return;
    }

    if (clientSecret) {
      return;
    }

    if (!stripePromise) {
      this.fetchStripePromise();
    }

    try {
      if (context === STRIPE_FORM_CONTEXT.concierge) {
        const response = await postStripeConciergeSetupIntent({ venue_id: venueId });

        const {
          client_secret: {
            client_secret, // eslint-disable-line camelcase
            stripe_account_id, // eslint-disable-line camelcase
          },
        } = response;

        this.setState({
          clientSecret: client_secret,
          stripeAccountId: stripe_account_id,
        });
      } else {
        const response = await postStripeSetupIntent(venueId);

        const {
          client_secret, // eslint-disable-line camelcase
        } = response;

        this.setState({
          clientSecret: client_secret,
        });
      }
    } catch (error) {
      this.setState({
        error,
      });
    }
  }

  componentDidUpdate() {
    const { stripePromise } = this.state;
    if (!stripePromise) this.fetchStripePromise();
  }

  fetchStripePromise = () => {
    const { stripePublishableKey } = this.props;
    const { stripeAccountId, clientSecret } = this.state;

    if (!clientSecret) return;

    this.setState({
      stripePromise: stripeAccountId
        ? loadStripe(stripePublishableKey, { stripeAccount: stripeAccountId })
        : loadStripe(stripePublishableKey),
    });
  }

  renderSubHeader = () => {
    const { context } = this.props;

    if (context === STRIPE_FORM_CONTEXT.profile) {
      return (
        <TranslationProvider translationKey="card_verification_hold_message">
          <div className="StripeForm__subtitle">To verify your card, our payment processor may place a temporary charge on your account, which will be released back to you within a few days.</div>
        </TranslationProvider>
      );
    }
    return (
      <TranslationProvider translationKey="venue_credit_card_requirement">
        <div className="StripeForm__subtitle">
          This venue requires a credit card to book because this reservation is either pre-paid, has pre-paid items, or may have a cancellation fee.
        </div>
      </TranslationProvider>
    );
  }

  render() {
    const {
      Analytics,
      AutomationMode,
      closeForm,
      matchMedia,
      redirectPath,
      withHeader,
      context,
    } = this.props;

    const { clientSecret, stripePromise, error } = this.state;

    if (!clientSecret || !stripePromise) {
      return null;
    }

    if (error) {
      return (
        <div className="StripeForm__error">
          Error:
          {error}
        </div>
      );
    }

    const options = {
      clientSecret,
      appearance: appearance(!matchMedia.md),
      fonts: [{
        cssSrc: '../../style/font-definitions.scss',
      }],
    };

    return (
      <div className="StripeForm">
        <Elements stripe={stripePromise} options={options}>
          {withHeader && (
          <div className="StripeForm__header">
            <TranslationProvider translationKey="add_credit_card">
              <h1>
                Add Credit Card
              </h1>
            </TranslationProvider>
            {this.renderSubHeader()}
          </div>
          )}

          <StripeAddCardForm
            Analytics={Analytics}
            AutomationMode={AutomationMode}
            closeForm={closeForm}
            redirectPath={redirectPath}
            context={context}
          />
        </Elements>
      </div>
    );
  }
}

StripeForm.displayName = 'StripeForm';
export default StripeForm;
